import React, { useEffect, useState } from "react";
import {
    createTheme,
    Step,
    StepConnector,
    StepIconProps,
    StepLabel,
    Stepper,
    styled,
    ThemeProvider
} from "@mui/material";
import {
    CheckCircle,
    CreditCard,
    Home,
    Inventory2,
    LocalShipping,
    LockClock,
    Backpack,
    CancelOutlined
} from "@mui/icons-material";
import styles from "../../styles/order_progress_styles.module.scss";
import { ReactComponent as Arrow } from "../../assets/icons/arrow-left.svg";
import { ReactComponent as Pencil } from "../../assets/icons/pencil.svg";
import { ReactComponent as Trash } from "../../assets/icons/trash.svg";
import { ReactComponent as Refund } from "../../assets/icons/refund.svg";
import { useMutation, useQuery } from "react-query";
import { IOrderDetail, IPostUpdateOrderStatus, IPostUpdateStatus, IAddressData, IUpdateAddressRequest } from "../../ts/interfaces/Order/interface";
import { getOrder, updateOrderStatus, updateStatus, updateOrderAddress } from "../../services/services";
import { useParams } from "react-router-dom";
import { BarLoader } from "react-spinners";
import { toast } from "react-toastify";
import ModalConfirm from "../../components/ModalConfirm/ModalConfirm";
import ModalEditAddress from "../../components/ModalEditAddress/ModalEditAddress";

const steps = [
    "Pedido Criado",
    "Aguardando Pagamento",
    "Pagamento Aprovado",
    "Em separação",
    "Pedido Embalado",
    "Enviado",
    "Entregue",
];

const StepIcon = (props: StepIconProps) => {
    const icons: { [index: number]: React.ReactElement } = {
        1: <CheckCircle color={props.active ? "primary" : props.completed ? "primary" : "disabled"} />,
        2: <LockClock color={props.active ? "primary" : props.completed ? "primary" : "disabled"} />,
        3: <CreditCard color={props.active ? "primary" : props.completed ? "primary" : "disabled"} />,
        4: <Inventory2 color={props.active ? "primary" : props.completed ? "primary" : "disabled"} />,
        5: <Backpack color={props.active ? "primary" : props.completed ? "primary" : "disabled"} />,
        6: <LocalShipping color={props.active ? "primary" : props.completed ? "primary" : "disabled"} />,
        7: <Home color={props.active ? "primary" : props.completed ? "primary" : "disabled"} />,
    };

    return icons[props.icon as number] || <CheckCircle />;
};

const CustomConnector = styled(StepConnector)(({ theme }) => ({
    "&.MuiStepConnector-root": {
        height: 4,
    },
    "& .MuiStepConnector-line": {
        borderColor: "#ddd",
        borderWidth: 2,
        transition: "all 0.3s ease-in-out",
    },
    "&.Mui-active .MuiStepConnector-line, &.Mui-completed .MuiStepConnector-line": {
        borderColor: "#FF9800",
        borderWidth: 5,
        boxShadow: "0px 2px 6px rgba(255, 152, 0, 0.5)",
    },
}));

const theme = createTheme({
    palette: {
        primary: {
            main: "#FF9800",
        },
        secondary: {
            main: "#000000",
        },
    },
});

const OrderProgress: React.FC = () => {
    const queryParams = useParams();

    const { data, isLoading, refetch } = useQuery<{
        data: IOrderDetail;
        status: number;
    }>(
        ["order-progress", queryParams?.id],
        () => getOrder(parseInt(queryParams?.id ?? "0")),
        {
            staleTime: Infinity,
            enabled: !!queryParams?.id,
        }
    );

    if (!isLoading && queryParams?.id && data?.status !== 200) {
        window.location.href = '/categories';
    }

    const [activeStep, setActiveStep] = useState<number>(1);
    const [openStatusModal, setOpenStatusModal] = useState<boolean>(false);
    const [openActionModal, setOpenActionModal] = useState<boolean>(false);
    const [dataModal, setDataModal] = useState<{ id: number, status: string } | null>(null);
    const [trackingNumber, setTrackingNumber] = useState<string>('');
    const [actionType, setActionType] = useState<'cancel' | 'refund' | null>(null);
    const [openAddressModal, setOpenAddressModal] = useState<boolean>(false);
    const [addressType, setAddressType] = useState<'shipping' | 'billing' | null>(null);
    const [addressData, setAddressData] = useState<IAddressData | null>(null);

    useEffect(() => {
        if (data?.data) {
            let index = 0;
            if (data.data.status === 'waiting_payment') {
                index = 1
            }
            if (data.data.status === 'paid') {
                index = 2
            }
            if (data.data.status === 'in_separation') {
                index = 3
            }
            if (data.data.status === 'waiting_for_carrier') {
                index = 4
            }
            if (data.data.status === 'in_transport') {
                index = 5
            }
            if (data.data.status === 'delivered') {
                index = 6
            }

            setActiveStep(index);
        }
    }, [data]);

    const UpdateStatusMutation = useMutation((data: IPostUpdateStatus) => {
        return updateStatus(data);
    }, {
        onSettled: (data) => {
            if (data?.data?.success) {
                refetch();
                toast.success("Status atualizado com sucesso");
            } else {
                toast.error("Erro ao atualizar status. " + data?.data?.msg);
            }
            setOpenStatusModal(false);
            setDataModal(null);
            setTrackingNumber('');
            setActionType(null);
        },
        onError: () => {
            toast.error("Erro ao atualizar status.");
        },
    })

    const UpdateOnlyStatusMutation = useMutation((data: IPostUpdateOrderStatus) => {
        return updateOrderStatus(data);
    }, {
        onSettled: (data) => {
            if (data?.data?.success) {
                refetch();
                toast.success("Status atualizado com sucesso");
            } else {
                toast.error("Erro ao atualizar status. " + data?.data?.msg);
            }
            setOpenActionModal(false);
            setDataModal(null);
        },
        onError: () => {
            toast.error("Erro ao atualizar status.");
        },
    })

    const UpdateAddressMutation = useMutation((data: IUpdateAddressRequest) => {
        return updateOrderAddress(data);
    }, {
        onSettled: (data) => {
            if (data?.data?.success) {
                refetch();
                toast.success("Endereço atualizado com sucesso");
            } else {
                toast.error("Erro ao atualizar endereço. " + data?.data?.msg);
            }
            setOpenAddressModal(false);
            setAddressType(null);
            setAddressData(null);
        },
        onError: () => {
            toast.error("Erro ao atualizar endereço.");
        },
    })

    const getButtonLabel = (orderStatus: string) => {
        switch (orderStatus) {
            case "new":
            case "waiting_payment":
                return "Marcar como pago";
            case "paid":
                return "Alterar para em separação";
            case "in_separation":
                return "Marcar como embalado";
            case "waiting_for_carrier":
                return "Marcar como em transporte";
            case "in_transport":
                return "Marcar como entregue";
            default:
                return null;
        }
    }

    const getModalLabel = (orderStatus: string) => {
        switch (orderStatus) {
            case "new":
            case "waiting_payment":
                return "Deseja marcar esse pedido como pago?";
            case "paid":
                return "Deseja alterar esse pedido para em separação?";
            case "in_separation":
                return "Deseja marcar esse pedido como embalado?";
            case "waiting_for_carrier":
                return "Deseja marcar esse pedido como em transporte?";
            case "in_transport":
                return "Deseja marcar esse pedido como entregue?";
            default:
                return null;
        }
    }

    const getActionFunction = (id: number, orderStatus: string) => {
        switch (orderStatus) {
            case "new":
            case "waiting_payment":
                return UpdateStatusMutation.mutate({
                    ids: [id],
                    status: 'paid'
                });
            case "paid":
                return UpdateOnlyStatusMutation.mutate({
                    id: id,
                    status: 'in_separation'
                });
            case "in_separation":
                return UpdateOnlyStatusMutation.mutate({
                    id: id,
                    status: 'waiting_for_carrier'
                });
            case "waiting_for_carrier":
                return UpdateOnlyStatusMutation.mutate({
                    id: id,
                    status: 'in_transport',
                    tracking_number: trackingNumber
                });
            case "in_transport":
                return UpdateOnlyStatusMutation.mutate({
                    id: id,
                    status: 'delivered'
                });
            default:
                return () => {
                };
        }
    }

    const openAddressEditModal = (type: 'shipping' | 'billing') => {
        if (!data?.data || data.data.status === 'cancelled' || data.data.status === 'refunded') return;

        const orderData = data.data;
        let addressInfo: IAddressData;

        if (type === 'shipping') {
            addressInfo = {
                street: orderData.shipping_street || '',
                number: orderData.shipping_number || '',
                neighborhood: orderData.shipping_neighborhood || '',
                complement: orderData.shipping_complement || '',
                city: orderData.shipping_city || '',
                state: orderData.shipping_state || '',
                zipcode: orderData.shipping_zipcode || ''
            };
        } else {
            addressInfo = {
                street: orderData.user_street_name || '',
                number: orderData.user_street_number || '',
                neighborhood: orderData.user_street_neighborhood || '',
                complement: orderData.user_street_complement || '',
                city: orderData.user_street_city || '',
                state: orderData.user_street_state || '',
                zipcode: orderData.user_street_zipcode || ''
            };
        }

        setAddressType(type);
        setAddressData(addressInfo);
        setOpenAddressModal(true);
    };

    const handleAddressSave = (addressData: IAddressData) => {
        if (!data?.data || !addressType) return;

        UpdateAddressMutation.mutate({
            order_id: data.data.id,
            address_type: addressType,
            address_data: addressData
        });
    };

    return (
        <>
            {isLoading || !data?.data ? (
                <div className={styles.alignCenter}>
                    <span>Carregando</span>
                    <BarLoader
                        color={"#ff7526"}
                        loading={isLoading}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                    />
                </div>
            ) : (
                <div className={styles.container}>
                    <div className={styles.title}>
                        <div className={styles.details}>
                            <div>
                                <a
                                    href="/orders"
                                    className={styles.btnReturn}
                                >
                                    <Arrow />
                                </a>
                                <h1>#{data.data.increment_id}</h1>
                            </div>
                            <div>
                                <a href="/orders">Pedidos</a>
                                <span>/</span>
                                <span>Progresso</span>
                                <span>/</span>
                                <span>#{data.data.increment_id} - {data.data.created_at}</span>
                            </div>
                        </div>

                        <div className={styles.actions}>
                            <button
                                className={`${styles.trash} ${data.data.status === 'cancelled' || data.data.status === 'refunded' ? styles.disabled : ''}`}
                                disabled={data.data.status === 'cancelled' || data.data.status === 'refunded'}
                                style={{ 
                                    cursor: data.data.status === 'cancelled' || data.data.status === 'refunded' ? 'not-allowed' : 'pointer',
                                    opacity: data.data.status === 'cancelled' || data.data.status === 'refunded' ? 0.5 : 1
                                }}
                                onClick={() => {
                                    setActionType('cancel');
                                    setOpenStatusModal(true);
                                }}>
                                <Trash /> Cancelar
                            </button>
                            <button
                                className={`${styles.refund} ${data.data.status === 'cancelled' || data.data.status === 'refunded' ? styles.disabled : ''}`}
                                disabled={data.data.status === 'cancelled' || data.data.status === 'refunded'}
                                style={{ 
                                    cursor: data.data.status === 'cancelled' || data.data.status === 'refunded' ? 'not-allowed' : 'pointer',
                                    opacity: data.data.status === 'cancelled' || data.data.status === 'refunded' ? 0.5 : 1
                                }}
                                onClick={() => {
                                    setActionType('refund');
                                    setOpenStatusModal(true);
                                }}>
                                <Refund /> Reembolsar
                            </button>
                        </div>
                    </div>
                    <div className={styles.content}>
                        <div className={styles.bigCard}>
                            <div className={styles.progress}>
                                <p className={styles.cardTitle}>Progresso</p>
                                {data.data.status === 'cancelled' || data.data.status === 'refunded' ?
                                    <div className={styles.orderCancelled}>
                                        <div>
                                            <CancelOutlined color="error" />
                                            <span>Pedido {data.data.status === 'cancelled' ? 'Cancelado' : 'Reembolsado'}</span>
                                        </div>
                                    </div>
                                    : <div>
                                        <ThemeProvider theme={theme}>
                                            <Stepper alternativeLabel activeStep={activeStep}
                                                connector={<CustomConnector />}>
                                                {steps.map((label, index) => (
                                                    <Step key={label} completed={index < activeStep}>
                                                        <StepLabel
                                                            StepIconComponent={StepIcon}>{label}</StepLabel>
                                                    </Step>
                                                ))}
                                            </Stepper>
                                        </ThemeProvider>
                                    </div>
                                }
                                <div className={styles.actions}>
                                    {getButtonLabel(data.data.status) &&
                                        <button
                                            onClick={() => {
                                                setOpenActionModal(true);
                                                setDataModal({
                                                    id: data.data.id,
                                                    status: data.data.status
                                                });
                                                setTrackingNumber(''); // Limpar o campo ao abrir o modal
                                            }}>{getButtonLabel(data.data.status)}</button>
                                    }
                                </div>
                            </div>
                            <div className={styles.products}>
                                <p className={styles.cardTitle}>Produtos</p>
                                <div className={styles.tableProducts}>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Item</th>
                                                <th>Tamanho</th>
                                                <th>Quantidade</th>
                                                <th>Preço</th>
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.data.items.map((item) => {
                                                return (
                                                    <tr key={item.id}>
                                                        <td className={styles.item}>
                                                            <img
                                                                src={item.image_url ?? ''}
                                                                alt={item.name} />
                                                            <div>
                                                                <p>{item.name}</p>
                                                                <a href={`/product/create/${item.productId}`}>SKU: {item.sku}</a>
                                                            </div>
                                                        </td>
                                                        <td>{item.size}</td>
                                                        <td>{item.quantity}</td>
                                                        <td>{item.price}</td>
                                                        <td>{item.totalPrice}</td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div className={styles.littleCard}>
                            <div className={styles.payment}>
                                <p className={styles.cardTitle}>Pagamento</p>
                                <div className={styles.details}>
                                    <div><span>Custo</span><span>{data.data.cost}</span></div>
                                    <div><span>Subtotal</span><span>{data.data.total_price}</span></div>
                                    <div><span>Desconto</span><span
                                        className={styles.discount}>-{data.data.discount}</span></div>
                                    <div><span>Frete</span><span>{data.data.shipping_price}</span></div>
                                    {/*<div><span>Cupom</span><span>PROMO10</span></div>*/}
                                </div>
                                <hr />
                                <div className={`${styles.details} ${styles.total}`}>
                                    <div><span>Total</span><span>{data.data.final_price}</span></div>
                                    <div><span>Lucro</span><span>{data.data.profit}</span></div>
                                </div>
                            </div>

                            <div className={styles.customer}>
                                <p className={styles.cardTitle}>Cliente</p>
                                <div className={styles.customerInfo}>
                                    <span className={styles.name}>{data.data.user_name}</span>
                                    <span className={styles.email}>{data.data.user_email}</span>
                                    <span>{data.data.user_phone_number}</span>
                                </div>
                                <div className={styles.shippingInfo}>
                                    <div>
                                        <p>Endereço de entrega</p>
                                        <Pencil 
                                            onClick={() => openAddressEditModal('shipping')} 
                                            style={{ 
                                                cursor: data.data.status === 'cancelled' || data.data.status === 'refunded' ? 'not-allowed' : 'pointer',
                                                opacity: data.data.status === 'cancelled' || data.data.status === 'refunded' ? 0.5 : 1
                                            }} 
                                        />
                                    </div>
                                    <span>{data.data.shipping_street}, {data.data.shipping_number ?? 'N/A'}</span>
                                    <span>{data.data.shipping_zipcode}</span>
                                    <span>{data.data.shipping_neighborhood}</span>
                                    <span>{data.data.shipping_city} - {data.data.shipping_state}</span>
                                    <span>{data.data.shipping_complement}</span>
                                </div>
                                <div className={styles.shippingInfo}>
                                    <div>
                                        <p>Endereço de cobrança</p>
                                        <Pencil 
                                            onClick={() => openAddressEditModal('billing')} 
                                            style={{ 
                                                cursor: data.data.status === 'cancelled' || data.data.status === 'refunded' ? 'not-allowed' : 'pointer',
                                                opacity: data.data.status === 'cancelled' || data.data.status === 'refunded' ? 0.5 : 1
                                            }} 
                                        />
                                    </div>
                                    <span>{data.data.user_street_name}, {data.data.user_street_number ?? 'N/A'}</span>
                                    <span>{data.data.user_street_zipcode}</span>
                                    <span>{data.data.user_street_neighborhood}</span>
                                    <span>{data.data.user_street_city} - {data.data.user_street_state}</span>
                                    <span>{data.data.shipping_complement}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {openStatusModal && (
                        <ModalConfirm
                            propsFunction={() => {
                                const status = actionType === 'cancel' ? 'cancelled' : 'refunded';
                                UpdateStatusMutation.mutate({
                                    ids: [parseInt(queryParams?.id ?? "0")],
                                    status: status
                                })
                            }}
                            open={openStatusModal}
                            setOpen={(open) => {
                                setOpenStatusModal(open);
                                if (!open) {
                                    setActionType(null);
                                }
                            }}
                            loading={UpdateStatusMutation.isLoading}
                            nomeButton={actionType === 'cancel' ? 'Sim' : 'Reembolsar'}
                        >
                            <span>
                                {actionType === 'cancel' 
                                    ? 'Tem certeza que deseja cancelar esse pedido?' 
                                    : 'Tem certeza que deseja reembolsar esse pedido?'
                                }
                            </span>
                        </ModalConfirm>
                    )}

                    {openActionModal && dataModal && (
                        <ModalConfirm
                            propsFunction={() => {
                                if (dataModal.status === 'waiting_for_carrier' && !trackingNumber.trim()) {
                                    toast.error('Por favor, digite o número de rastreamento');
                                    return;
                                }
                                getActionFunction(dataModal.id, dataModal.status)
                            }}
                            open={openActionModal}
                            setOpen={setOpenActionModal}
                            loading={UpdateStatusMutation.isLoading || UpdateOnlyStatusMutation.isLoading}
                        >
                            <span>{getModalLabel(dataModal.status)}</span>
                            {dataModal.status === 'waiting_for_carrier' && (
                                <div style={{ marginTop: '16px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                                        Número de Rastreamento:
                                    </label>
                                    <input
                                        type="text"
                                        value={trackingNumber}
                                        onChange={(e) => setTrackingNumber(e.target.value)}
                                        placeholder="Digite o número de rastreamento"
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                        required
                                    />
                                </div>
                            )}
                        </ModalConfirm>
                    )}

                    {openAddressModal && addressType && addressData && (
                        <ModalEditAddress
                            isOpen={openAddressModal}
                            onClose={() => {
                                setOpenAddressModal(false);
                                setAddressType(null);
                                setAddressData(null);
                            }}
                            onSave={handleAddressSave}
                            addressType={addressType}
                            initialData={addressData}
                            loading={UpdateAddressMutation.isLoading}
                        />
                    )}
                </div>
            )}
        </>
    );
};

export default OrderProgress;
